import type { CreatorOnboardingStep } from "@/api/creatorOnboarding";
import { getBankFieldRequirements } from "./bankRequirements";

export const ONBOARDING_STEPS: CreatorOnboardingStep[] = ["socials", "profile", "bank"];

export type ProfileValidationField =
	| "legalFirstName"
	| "legalLastName"
	| "country"
	| "mobileCountryCode"
	| "mobileNumber"
	| "addressLine1"
	| "addressLine2"
	| "city"
	| "state"
	| "postcode";

export type ProfileValidationErrors = Partial<Record<ProfileValidationField, string>>;

export const getProfileValidationErrors = (
	profile: Record<ProfileValidationField, string>,
): ProfileValidationErrors => {
	const errors: ProfileValidationErrors = {};
	const required: Array<[ProfileValidationField, string]> = [
		["legalFirstName", "First name is required."],
		["legalLastName", "Last name is required."],
		["country", "Country is required."],
		["mobileCountryCode", "Calling code is required."],
		["mobileNumber", "Mobile number is required."],
		["addressLine1", "Address line 1 is required."],
		["city", "City is required."],
		["state", "State or territory is required."],
		["postcode", "Postcode is required."],
	];
	required.forEach(([field, message]) => {
		if (!profile[field].trim()) errors[field] = message;
	});
	return errors;
};

export const isProfileComplete = (profile: Record<ProfileValidationField, string>): boolean =>
	Object.keys(getProfileValidationErrors(profile)).length === 0;

export type BankValidationValues = {
	accountName: string;
	accountNumber: string;
	bankName: string;
	bsb: string;
	branchCode: string;
	branchName: string;
	swiftBic: string;
	abaRoutingNumber: string;
};

export type BankValidationErrors = Partial<Record<keyof BankValidationValues, string>>;

export const getBankValidationErrors = (country: string, bank: BankValidationValues): BankValidationErrors => {
	const errors: BankValidationErrors = {};
	const requirements = getBankFieldRequirements(country);
	if (!bank.accountName.trim()) errors.accountName = "Account name is required.";
	if (!bank.accountNumber.trim()) errors.accountNumber = "Account number is required.";
	if (!bank.bankName.trim()) errors.bankName = "Bank name is required.";
	if (requirements.bsb) {
		if (!bank.bsb.trim()) errors.bsb = "BSB Number is required.";
		else if (!/^\d{6}$/.test(bank.bsb.replace(/[\s-]/g, ""))) errors.bsb = "BSB Number must contain 6 digits.";
	}
	if (requirements.abaRoutingNumber) {
		if (!bank.abaRoutingNumber.trim()) errors.abaRoutingNumber = "ABA Routing Number is required.";
		else if (!/^\d{9}$/.test(bank.abaRoutingNumber)) {
			errors.abaRoutingNumber = "ABA Routing Number must contain 9 digits.";
		}
	}
	if (requirements.branchCode && !bank.branchCode.trim()) errors.branchCode = "Branch Code is required.";
	if (requirements.branchName && !bank.branchName.trim()) errors.branchName = "Branch Name is required.";
	if (requirements.swiftBic) {
		if (!bank.swiftBic.trim()) errors.swiftBic = "BIC/SWIFT Code is required.";
		else if (!/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/i.test(bank.swiftBic.trim())) {
			errors.swiftBic = "Enter a valid 8- or 11-character BIC/SWIFT code.";
		}
	}
	return errors;
};

export const isBankComplete = (country: string, bank: BankValidationValues): boolean =>
	Object.keys(getBankValidationErrors(country, bank)).length === 0;
